import React, { useEffect, useState, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Link } from 'react-router-dom';
import { db } from '../firebaseConfig';

function RSVP() {
  const [familyGuests, setFamilyGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRefs = useRef({});

  useEffect(() => {
    const fetchGuests = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user || !user.phoneNumber) return;

      const invitesRef = collection(db, 'invites');
      const userSnap = await getDocs(query(invitesRef, where('phoneNumber', '==', user.phoneNumber)));
      if (userSnap.empty) return;

      const userGuest = userSnap.docs[0].data();
      const clan = userGuest.familyClan;

      const clanSnap = await getDocs(query(invitesRef, where('familyClan', '==', clan)));
      const guests = clanSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFamilyGuests(guests);
      setLoading(false);
    };

    fetchGuests();
  }, []);

  const handleRSVP = async (guestId, status) => {
    await updateDoc(doc(db, 'invites', guestId), { rsvp: status });

    setFamilyGuests(prev =>
      prev.map(g => (g.id === guestId ? { ...g, rsvp: status } : g))
    );
  };

  const handlePhotoUpload = async (guestId, file) => {
    if (!file) return;

    try {
      const storage = getStorage();
      const photoRef = ref(storage, `guest_photos/${guestId}`);
      await uploadBytes(photoRef, file);
      const url = await getDownloadURL(photoRef);

      // Update Firestore
      const guestRef = doc(db, 'invites', guestId);
      await updateDoc(guestRef, { photoUrl: url });

      // Update local state
      setFamilyGuests(prev =>
        prev.map(g => (g.id === guestId ? { ...g, photoUrl: url } : g))
      );
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };

  if (loading) return <div className="page-container" style={{ textAlign: 'center', marginTop: '40px', color: '#fff' }}>Loading your family group...</div>;

  return (
    <div className="page-container">
      <h2 className="page-title">RSVP</h2>
      <p className="page-subtitle">Please let us know if you'll be joining us on our special day!</p>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        justifyContent: 'center',
        padding: '0 10px'
      }}>
        {familyGuests.map(guest => (
          <div key={guest.id} className="card" style={{
            width: 'clamp(200px, 40vw, 250px)',
            textAlign: 'center'
          }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={guest.photoUrl || 'https://placehold.co/150x150?text=Guest'}
                alt={guest.name}
                onClick={() => fileInputRefs.current[guest.id]?.click()}
                style={{
                  width: 'clamp(80px, 20vw, 100px)',
                  height: 'clamp(80px, 20vw, 100px)',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  marginBottom: '10px',
                  border: '2px solid white',
                  backgroundColor: '#ccc',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
                title="Click to update photo"
              />
              <input
                ref={el => fileInputRefs.current[guest.id] = el}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handlePhotoUpload(guest.id, file);
                  }
                  // Reset input so same file can be selected again
                  e.target.value = '';
                }}
              />
            </div>
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '10px',
              fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
              fontFamily: 'var(--font-body)'
            }}>{guest.name}</div>
            <button
              onClick={() => handleRSVP(guest.id, 'yes')}
              className="button-primary"
              style={{
                marginBottom: '8px',
                width: '100%',
                backgroundColor: guest.rsvp === 'yes' ? 'var(--button-success)' : 'var(--button-primary)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                borderColor: guest.rsvp === 'yes' ? 'var(--accent-gold)' : 'rgba(255, 255, 255, 0.2)'
              }}
            >
              Joyfully accepts
            </button>
            <button
              onClick={() => handleRSVP(guest.id, 'no')}
              className="button-primary"
              style={{
                width: '100%',
                backgroundColor: guest.rsvp === 'no' ? 'var(--button-danger)' : 'var(--button-primary)',
                fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                borderColor: guest.rsvp === 'no' ? 'rgba(200, 90, 90, 0.5)' : 'rgba(255, 255, 255, 0.2)'
              }}
            >
              Regretfully declines
            </button>
          </div>
        ))}
      </div>
      {familyGuests.some(g => !g.eveningGuest) && (
        <div style={{
          marginTop: '40px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <Link
            to="/menu-choices"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: 'var(--button-primary)',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
              fontWeight: '600',
              fontFamily: 'var(--font-body)',
              transition: 'background-color 0.3s ease',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--button-hover)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--button-primary)';
            }}
          >
            Select Your Meals
          </Link>
        </div>
      )}
    </div>
  );
}

export default RSVP;